import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { cloneDeep } from "lodash";
// Components
import { Button } from "components/Buttons";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "layouts/Dialog";
// Registry
import { getQuizPageEditEntry } from "./pageEditRegistry";
// Context
import { usePausePreviewSync } from "./PreviewSyncContext";
import { useCommitVersion } from "./VersionHistoryContext";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Page key from QuizAgentChatScreen's `screens` list — matches the
   *  identifiers used by QuizFrame's `page` postMessage prop. */
  page: string;
  /** UUID of the specific page entry (Question / Banner / Timer / Waiting)
   *  when the page key represents a dynamic, many-instance screen. */
  pageRef?: string;
  quizId?: string;
  /** When provided (new-page flow), Cancel reverts to this snapshot instead
   *  of the one captured on mount — which would already include the new page. */
  preAddSnapshot?: any;
}

const QuizPageEditDialog:FC<Props> = ({ open, onClose, page, pageRef, quizId, preAddSnapshot }) => {
  const { t } = useTranslation();

  // Freeze QuizAgentDialog's iframe-feeding quizHash while this dialog is
  // mounted — typing inside the form would otherwise re-postMessage every
  // preview iframe on every keystroke. On unmount the parent runs one
  // forced recompute so the previews catch up with the new values.
  usePausePreviewSync();

  const commitVersion = useCommitVersion();

  const methods = useFormContext<any>();

  // Snapshot the whole form on mount so Cancel can revert anything the user
  // touched inside the dialog. We use useState's lazy initializer (runs
  // exactly once) AND deepClone — methods.getValues() returns a reference
  // to RHF's internal _formValues, which RHF mutates in place on every
  // keystroke. Without the clone, `snapshot` would end up identical to the
  // live (modified) values by the time Cancel fires, and reset(snapshot)
  // would be a no-op.
  const [snapshot] = useState(() => preAddSnapshot ?? cloneDeep(methods.getValues()));

  const entry = getQuizPageEditEntry(page);
  const Form = entry?.Form ?? null;
  const title = entry?.label ?? page;

  // Any dismissal path (Cancel, backdrop, ESC, X) must revert the form.
  // Keep a single handler so the behaviour is consistent everywhere.
  const handleClose = () => {
    // Restore the form to its pre-dialog state. RHF.reset clears dirty/touched
    // flags too, which is what we want here. After reset, onClose unmounts
    // the dialog → usePausePreviewSync cleanup runs resume() → previews
    // recompute against the restored values.
    methods.reset(snapshot);
    onClose();
  };

  // Commit a version BEFORE unmounting — pushVersion snapshots getValues(),
  // and the dialog is still mounted (and paused) at this point, so the
  // snapshot reflects exactly what's on screen when Apply was clicked.
  const handleApply = () => {
    commitVersion(`Edited: ${title}`);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <DialogTitle title={title} onClose={handleClose} />
      <DialogContent>
        {Form ? (
          <Form pageRef={pageRef} quizId={quizId} />
        ) : (
          <p className="text-white text-sm opacity-60 py-6 text-center">
            {/* Stub for pages whose edit form hasn't been wired up yet. */}
            This screen is not editable yet.
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} className="mr-2">
          {t('buttons.cancel')}
        </Button>
        <Button variant="contained" onClick={handleApply}>
          {t('buttons.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuizPageEditDialog;
